import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  Skeleton,
  Tooltip,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { Check, Tune } from "@mui/icons-material";

// Feature status values
export type FeatureStatus = "enabled" | "locked" | "hidden";

// Tier data interface matching backend response
export interface TierData {
  id: string;
  name: string;
  description: string;
  showPrice?: boolean;
  price?: number;
  priceLabel?: string;
  priceSubLabel?: string;
  priceFromBackend?: boolean;
  isContactSales?: boolean;
  salesEmail?: string;
  features?: string[];
  features_status?: Record<string, FeatureStatus>;
  creditLimit?: number;
  creditsLabel?: string;
  baseSeats?: number;
  baseCompanies?: number;
  baseCreditLimit?: number;
  hasConfiguration?: boolean;
  creditPackageIncrement?: number;
  pricing?: {
    annual: number;
    monthly: number;
  };
  firstMonthSpecial?: {
    price: number;
    label: string;
  } | null;
}

// Configuration for a tier (seats, companies, credit packages)
export interface TierConfig {
  seats?: number;
  companies?: number;
  creditPackages?: number;
}

// Calculated pricing from backend
export interface CalculatedPricing {
  pricePerUnit: number;
  totalAmount: number;
  monthlyPrice?: number;
}

// Component for tier description with 2-line truncation and conditional tooltip
const TruncatedDescription: React.FC<{ description: string }> = ({
  description,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const element = textRef.current;
    if (element) {
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [checkTruncation, description]);

  const textElement = (
    <Typography
      ref={textRef}
      variant="body2"
      color="text.secondary"
      sx={{
        mb: 2,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minHeight: "2.5em",
      }}
    >
      {description}
    </Typography>
  );

  if (isTruncated) {
    return (
      <Tooltip title={description} arrow>
        {textElement}
      </Tooltip>
    );
  }

  return textElement;
};

// Props for display-only mode (Pricing page)
interface DisplayModeProps {
  mode: "display";
  tier: TierData;
  billingPeriod: "annual" | "monthly";
  onSelectPlan: (tier: TierData) => void;
}

// Props for interactive mode (SubscriptionDialog)
interface InteractiveModeProps {
  mode: "interactive";
  tier: TierData;
  billingPeriod: "annual" | "monthly";
  tierConfig: TierConfig;
  calculatedPricing?: CalculatedPricing | null;
  pricingLoading?: boolean;
  isSelected?: boolean;
  isCurrentPlan?: boolean;
  hasExistingSubscription?: boolean;
  onSelectPlan: (tierId: string) => void;
  onOpenConfig?: (event: React.MouseEvent<HTMLButtonElement>, tierId: string) => void;
  onDowngradeClick?: (tierId: string) => void;
}

type SubscriptionTierCardProps = DisplayModeProps | InteractiveModeProps;

export const SubscriptionTierCard: React.FC<SubscriptionTierCardProps> = (props) => {
  const { tier, billingPeriod } = props;

  // Get the tier config based on mode
  const getTierConfig = (): TierConfig => {
    if (props.mode === "interactive") {
      return props.tierConfig;
    }
    return {};
  };

  const tierConfig = getTierConfig();

  // Helper to process feature placeholders
  const processFeature = (feature: string): string => {
    let displayFeature = feature;

    if (tier.hasConfiguration) {
      const companies = tierConfig.companies || tier.baseCompanies || 0;
      const seats = tierConfig.seats || tier.baseSeats || 1;
      const creditPackages = tierConfig.creditPackages || 0;

      if (feature.includes("{companies}")) {
        displayFeature = displayFeature
          .replace("{companies}", companies.toString())
          .replace("{companiesLabel}", companies === 1 ? "company" : "companies");
      }
      if (feature.includes("{seats}")) {
        displayFeature = displayFeature
          .replace("{seats}", seats.toString())
          .replace("{seatsLabel}", seats === 1 ? "seat" : "seats");
      }
      if (feature.includes("{totalCredits}")) {
        const totalCredits =
          (tier.baseCreditLimit || 0) +
          creditPackages * (tier.creditPackageIncrement || 0);
        displayFeature = displayFeature.replace(
          "{totalCredits}",
          totalCredits.toLocaleString()
        );
      }
    }

    return displayFeature;
  };

  // Render features list - matches original SubscriptionDialog exactly
  const renderFeatures = () => {
    if (!tier.features || tier.features.length === 0) return null;

    return tier.features.map((feature: string, index: number) => {
      const displayFeature = processFeature(feature);

      return (
        <Typography
          key={index}
          variant={index === 0 ? "body2" : "body1"}
          sx={{
            ...(index === 0
              ? { mb: 2, fontWeight: 500 }
              : {
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }),
          }}
        >
          {index > 0 && (
            <Check sx={{ mr: 1, color: "primary.main" }} fontSize="small" />
          )}
          {displayFeature}
        </Typography>
      );
    });
  };

  // Common table cell styles for consistent layout
  const tableCellStyle = {
    border: "none",
    padding: 0,
    paddingBottom: 1,
    verticalAlign: "top",
  };

  // Fixed row heights to ensure alignment across cards
  const rowHeights = {
    name: 40,
    description: 56,
    price: 56,
    config: 24,
    credits: 24,
    total: 28,
    options: 48,
  };

  // Render price section - uses table for consistent alignment across cards
  const renderPriceSection = () => {
    if (!tier.showPrice) return null;

    // Enterprise / Contact Sales tier
    if (tier.isContactSales) {
      return (
        <Table sx={{ tableLayout: "fixed" }}>
          <TableBody>
            {/* Row 1: Tier Name */}
            <TableRow>
              <TableCell sx={{ ...tableCellStyle, height: rowHeights.name }}>
                <Typography variant="h5">{tier.name}</Typography>
              </TableCell>
            </TableRow>
            {/* Row 2: Description */}
            <TableRow>
              <TableCell sx={{ ...tableCellStyle, height: rowHeights.description }}>
                <TruncatedDescription description={tier.description} />
              </TableCell>
            </TableRow>
            {/* Row 3: Price */}
            <TableRow>
              <TableCell sx={{ ...tableCellStyle, height: rowHeights.price }}>
                <Box sx={{ display: "flex", alignItems: "baseline" }}>
                  <Typography variant="h6">{tier.priceLabel}</Typography>
                </Box>
              </TableCell>
            </TableRow>
            {/* Row 4: Price sublabel / Config */}
            <TableRow>
              <TableCell sx={{ ...tableCellStyle, height: rowHeights.config }}>
                {tier.priceSubLabel && (
                  <Typography variant="body2" color="text.secondary">
                    {tier.priceSubLabel}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
            {/* Row 5: Credits info */}
            <TableRow>
              <TableCell sx={{ ...tableCellStyle, height: rowHeights.credits }}>
                {(tier.creditLimit || tier.baseCreditLimit) && (
                  <Typography variant="caption" color="text.secondary">
                    Custom credits/month
                  </Typography>
                )}
              </TableCell>
            </TableRow>
            {/* Row 6: Total */}
            <TableRow>
              <TableCell sx={{ ...tableCellStyle, height: rowHeights.total }}></TableCell>
            </TableRow>
            {/* Row 7: Options button */}
            <TableRow>
              <TableCell sx={{ ...tableCellStyle, height: rowHeights.options }}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      );
    }

    // Price from backend (paid tiers)
    if (tier.priceFromBackend) {
      if (props.mode === "interactive") {
        const { calculatedPricing, pricingLoading } = props;

        // Check for first month special (only for monthly billing)
        const hasFirstMonthSpecialInteractive = !!tier.firstMonthSpecial;

        return (
          <Table sx={{ tableLayout: "fixed" }}>
            <TableBody>
              {/* Row 1: Tier Name */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.name }}>
                  <Typography variant="h5">{tier.name}</Typography>
                </TableCell>
              </TableRow>
              {/* Row 2: Description */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.description }}>
                  <TruncatedDescription description={tier.description} />
                </TableCell>
              </TableRow>
              {/* Row 3: Price */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: hasFirstMonthSpecialInteractive ? rowHeights.price + 24 : rowHeights.price }}>
                  {pricingLoading ? (
                    <Typography variant="h3" sx={{ color: "text.secondary" }}>
                      Loading...
                    </Typography>
                  ) : hasFirstMonthSpecialInteractive ? (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "baseline" }}>
                        <Typography
                          variant="h5"
                          sx={{
                            textDecoration: "line-through",
                            color: "text.secondary",
                          }}
                        >
                          {calculatedPricing
                            ? `$${calculatedPricing.pricePerUnit}`
                            : "..."}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline" }}>
                        <Typography variant="h3" color="primary.main">
                          ${tier.firstMonthSpecial!.price}
                        </Typography>
                        <Typography variant="body1" sx={{ ml: 1 }} color="primary.main">
                          {tier.firstMonthSpecial!.label}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "baseline" }}>
                      <Typography variant="h3">
                        {calculatedPricing
                          ? `$${calculatedPricing.pricePerUnit}`
                          : "..."}
                      </Typography>
                      <Typography variant="body1" sx={{ ml: 1 }}>
                        per month
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
              {/* Row 4: Configuration summary (companies/seats) */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.config }}>
                  {(tier.baseCompanies || tier.baseSeats) && (
                    <Typography variant="caption" color="text.secondary">
                      {tierConfig.companies || tier.baseCompanies || 0}{" "}
                      {(tierConfig.companies || tier.baseCompanies || 0) === 1
                        ? "company"
                        : "companies"}{" "}
                      • {tierConfig.seats || tier.baseSeats || 1}{" "}
                      {(tierConfig.seats || tier.baseSeats || 1) === 1
                        ? "seat"
                        : "seats"}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
              {/* Row 5: Credits info */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.credits }}>
                  {(tier.baseCompanies || tier.baseSeats) ? (
                    <Typography variant="caption" color="text.secondary">
                      {(
                        (tier.baseCreditLimit || tier.creditLimit || 0) +
                        (tierConfig.creditPackages || 0) *
                          (tier.creditPackageIncrement || 0)
                      ).toLocaleString()}{" "}
                      credits/month
                    </Typography>
                  ) : tier.creditLimit ? (
                    <Typography variant="caption" color="text.secondary">
                      {tier.creditLimit.toLocaleString()} credits/month
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
              {/* Row 6: Total */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.total }}>
                  {(tier.hasConfiguration || tier.creditsLabel) && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "right" }}
                    >
                      Total:{" "}
                      <strong>
                        {pricingLoading
                          ? "Loading..."
                          : calculatedPricing
                          ? billingPeriod === "annual"
                            ? `$${calculatedPricing.totalAmount}/year`
                            : `$${calculatedPricing.totalAmount}/month`
                          : "..."}
                      </strong>
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
              {/* Row 7: Options button */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.options }}>
                  {tier.hasConfiguration && props.onOpenConfig && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={(e) => props.onOpenConfig!(e, tier.id)}
                      startIcon={<Tune />}
                    >
                      Options
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        );
      } else {
        // Display mode - use tier.pricing directly (for public pricing page)
        const price =
          billingPeriod === "annual"
            ? tier.pricing?.annual
            : tier.pricing?.monthly;

        // Check for first month special
        const hasFirstMonthSpecial = !!tier.firstMonthSpecial;

        return (
          <Table sx={{ tableLayout: "fixed" }}>
            <TableBody>
              {/* Row 1: Tier Name */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.name }}>
                  <Typography variant="h5">{tier.name}</Typography>
                </TableCell>
              </TableRow>
              {/* Row 2: Description */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.description }}>
                  <TruncatedDescription description={tier.description} />
                </TableCell>
              </TableRow>
              {/* Row 3: Price */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: hasFirstMonthSpecial ? rowHeights.price + 24 : rowHeights.price }}>
                  {hasFirstMonthSpecial ? (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "baseline" }}>
                        <Typography
                          variant="h5"
                          sx={{
                            textDecoration: "line-through",
                            color: "text.secondary",
                          }}
                        >
                          ${Math.round(price || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline" }}>
                        <Typography variant="h3" color="primary.main">
                          ${tier.firstMonthSpecial!.price}
                        </Typography>
                        <Typography variant="body1" sx={{ ml: 1 }} color="primary.main">
                          {tier.firstMonthSpecial!.label}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "baseline" }}>
                      <Typography variant="h3">
                        {price !== undefined ? `$${Math.round(price)}` : "..."}
                      </Typography>
                      <Typography variant="body1" sx={{ ml: 1 }}>
                        per month
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
              {/* Row 4: Configuration summary (companies/seats) */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.config }}>
                  {(tier.baseCompanies || tier.baseSeats) && (
                    <Typography variant="caption" color="text.secondary">
                      {tier.baseCompanies || 0}{" "}
                      {(tier.baseCompanies || 0) === 1 ? "company" : "companies"} •{" "}
                      {tier.baseSeats || 1}{" "}
                      {(tier.baseSeats || 1) === 1 ? "seat" : "seats"}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
              {/* Row 5: Credits info */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.credits }}>
                  {(tier.baseCompanies || tier.baseSeats) ? (
                    <Typography variant="caption" color="text.secondary">
                      {(tier.baseCreditLimit || tier.creditLimit || 0).toLocaleString()}{" "}
                      credits/month
                    </Typography>
                  ) : tier.creditLimit ? (
                    <Typography variant="caption" color="text.secondary">
                      {tier.creditLimit.toLocaleString()} credits/month
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
              {/* Row 6: Total */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.total }}></TableCell>
              </TableRow>
              {/* Row 7: Options button */}
              <TableRow>
                <TableCell sx={{ ...tableCellStyle, height: rowHeights.options }}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        );
      }
    }

    // Free tier
    return (
      <Table sx={{ tableLayout: "fixed" }}>
        <TableBody>
          {/* Row 1: Tier Name */}
          <TableRow>
            <TableCell sx={{ ...tableCellStyle, height: rowHeights.name }}>
              <Typography variant="h5">{tier.name}</Typography>
            </TableCell>
          </TableRow>
          {/* Row 2: Description */}
          <TableRow>
            <TableCell sx={{ ...tableCellStyle, height: rowHeights.description }}>
              <TruncatedDescription description={tier.description} />
            </TableCell>
          </TableRow>
          {/* Row 3: Price */}
          <TableRow>
            <TableCell sx={{ ...tableCellStyle, height: rowHeights.price }}>
              <Box sx={{ display: "flex", alignItems: "baseline" }}>
                <Typography variant="h3">${tier.price ?? 0}</Typography>
                <Typography variant="body1" sx={{ ml: 1 }}>
                  {tier.priceLabel}
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
          {/* Row 4: Configuration summary (companies/seats) */}
          <TableRow>
            <TableCell sx={{ ...tableCellStyle, height: rowHeights.config }}>
              {(tier.baseCompanies || tier.baseSeats) && (
                <Typography variant="caption" color="text.secondary">
                  {tierConfig.companies || tier.baseCompanies || 0}{" "}
                  {(tierConfig.companies || tier.baseCompanies || 0) === 1
                    ? "company"
                    : "companies"}{" "}
                  • {tierConfig.seats || tier.baseSeats || 1}{" "}
                  {(tierConfig.seats || tier.baseSeats || 1) === 1 ? "seat" : "seats"}
                </Typography>
              )}
            </TableCell>
          </TableRow>
          {/* Row 5: Credits info */}
          <TableRow>
            <TableCell sx={{ ...tableCellStyle, height: rowHeights.credits }}>
              {(tier.baseCompanies || tier.baseSeats) ? (
                <Typography variant="caption" color="text.secondary">
                  {(
                    (tier.baseCreditLimit || tier.creditLimit || 0) +
                    (tierConfig.creditPackages || 0) *
                      (tier.creditPackageIncrement || 0)
                  ).toLocaleString()}{" "}
                  credits/month
                </Typography>
              ) : (tier.creditLimit || tier.baseCreditLimit) ? (
                <Typography variant="caption" color="text.secondary">
                  {(tier.creditLimit || tier.baseCreditLimit || 0).toLocaleString()}{" "}
                  credits/month
                </Typography>
              ) : null}
            </TableCell>
          </TableRow>
          {/* Row 6: Total */}
          <TableRow>
            <TableCell sx={{ ...tableCellStyle, height: rowHeights.total }}></TableCell>
          </TableRow>
          {/* Row 7: Options button */}
          <TableRow>
            <TableCell sx={{ ...tableCellStyle, height: rowHeights.options }}></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  // Render action button - matches original SubscriptionDialog exactly for interactive mode
  const renderActionButton = () => {
    if (tier.isContactSales) {
      return (
        <Button
          variant="outlined"
          component="a"
          href={`mailto:${tier.salesEmail}`}
          fullWidth
          sx={{ mt: 2 }}
        >
          Contact Sales
        </Button>
      );
    }

    if (props.mode === "interactive") {
      const { isSelected, isCurrentPlan, hasExistingSubscription, onDowngradeClick } = props;

      return (
        <Button
          color={isSelected ? "primary" : "inherit"}
          variant={isSelected ? "contained" : "outlined"}
          onClick={() => {
            if (tier.id === "free" && hasExistingSubscription && onDowngradeClick) {
              onDowngradeClick(tier.id);
            } else {
              props.onSelectPlan(tier.id);
            }
          }}
          fullWidth
          sx={{ mt: 2 }}
          startIcon={
            isSelected ? (
              <Check />
            ) : isCurrentPlan ? (
              <Check />
            ) : null
          }
        >
          {isSelected
            ? "Selected"
            : isCurrentPlan
            ? "Current Plan"
            : "Select Plan"}
        </Button>
      );
    }

    // Display mode - simple buttons for pricing page
    return (
      <Button
        variant={tier.id === "free" ? "outlined" : "contained"}
        color="primary"
        onClick={() => (props as DisplayModeProps).onSelectPlan(tier)}
        fullWidth
        sx={{ mt: 2 }}
      >
        Get Started
      </Button>
    );
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {renderPriceSection()}
      {renderActionButton()}
      <Divider sx={{ my: 3 }} />
      {renderFeatures()}
    </Card>
  );
};

// Skeleton loading card - matches original SubscriptionDialog exactly
export const SubscriptionTierCardSkeleton: React.FC = () => (
  <Card
    variant="outlined"
    sx={{
      p: 3,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ mb: 2 }}>
      <Skeleton variant="text" width="60%" height={32} />
      <Skeleton variant="text" width="80%" height={20} />
    </Box>
    <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
      <Skeleton variant="text" width={100} height={48} />
      <Skeleton variant="text" width={80} height={20} sx={{ ml: 1 }} />
    </Box>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={36}
      sx={{ mt: 2, borderRadius: 1 }}
    />
    <Divider sx={{ my: 3 }} />
    <Box sx={{ flexGrow: 1 }}>
      {[1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
          <Skeleton variant="text" width="90%" />
        </Box>
      ))}
    </Box>
  </Card>
);

export default SubscriptionTierCard;
